"use client";

import React from "react";
import { ArrowRight, BookOpen, ChevronLeft, ChevronRight, Loader } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="overflow-hidden border-border/50 shadow-md">
            <div className="aspect-[16/10] w-full animate-pulse bg-muted" />
            <CardHeader className="space-y-3">
              <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
              <div className="h-6 w-3/4 animate-pulse rounded bg-muted" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-4 w-full animate-pulse rounded bg-muted" />
                <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border p-12 text-center">
        <p className="mb-4 text-muted-foreground">Unable to load the latest buying guides.</p>
        <Button onClick={refetch} variant="outline" className="rounded-lg border-primary text-primary hover:bg-primary/5">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border p-12 text-center">
        <BookOpen className="mb-4 size-12 text-muted-foreground/50" />
        <h3 className="mb-2 text-xl font-semibold font-default text-primary">No Guides Found</h3>
        <p className="text-muted-foreground">Check back later for expert home security advice.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-border/50 bg-card shadow-md transition-all duration-300 hover:shadow-xl hover:-translate-y-1 rounded-2xl">
            <Link to={post.link || "#"} className="block overflow-hidden">
              <div className="aspect-[16/10] w-full overflow-hidden bg-muted">
                <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687986/img/security_system_guide.png"} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
            </Link>
            <CardHeader className="flex-none pb-4">
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-widest">
                {post.categories && post.categories.length > 0 && <span className="rounded-md bg-destructive px-2.5 py-0.5 text-destructive-foreground">
                    {post.categories[0].name}
                  </span>}
                {post.date && <>
                    <span className="text-muted-foreground/50">•</span>
                    <span className="text-muted-foreground">
                      {new Date(post.date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric"
                })}
                    </span>
                  </>}
              </div>
              <Link to={post.link || "#"} className="block">
                <CardTitle className="line-clamp-2 text-xl font-bold font-default leading-tight transition-colors group-hover:text-primary">
                  {post.title}
                </CardTitle>
              </Link>
            </CardHeader>
            <CardContent className="flex-grow">
              <CardDescription className="line-clamp-3 text-sm leading-relaxed text-muted-foreground font-default">
                {post.excerpt?.replace(/(<([^>]+)>)/gi, "") || "Expert analysis and hands-on testing to help you choose the perfect security solution for your family home."}
              </CardDescription>
            </CardContent>
            <CardFooter className="flex-none pt-0">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="ghost" className="group/btn w-full justify-between px-0 hover:bg-transparent text-primary hover:text-primary/80">
                  <span className="font-bold font-default">Read Full Guide</span>
                  <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                </Button>
              </Link>
            </CardFooter>
          </Card>)}
      </div>

      {totalPages > 1 && <div className="flex items-center justify-center gap-4 pt-8">
          <Button variant="outline" size="icon" onClick={prevPage} disabled={!hasPrev} aria-label="Previous page" className="rounded-lg border-border hover:bg-muted">
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-sm font-semibold font-default text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button variant="outline" size="icon" onClick={nextPage} disabled={!hasNext} aria-label="Next page" className="rounded-lg border-border hover:bg-muted">
            <ChevronRight className="size-4" />
          </Button>
        </div>}
    </div>;
}
export default function ExpertBuyingGuides() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="233771" id="expert-buying-guides" className="bg-background text-foreground py-24 md:py-32">
      <div className="container mx-auto px-4 md:px-8">
        <div className="mb-12 max-w-3xl md:mb-16">
          <span className="mb-4 inline-block text-xs font-bold uppercase tracking-widest text-destructive">
            Expert Research
          </span>
          <h2 className="mb-6 font-default text-4xl font-bold tracking-tighter text-primary md:text-5xl lg:text-6xl">
            Expert Buying Guides
          </h2>
          <p className="text-lg text-muted-foreground md:text-xl font-default leading-relaxed">
            In-depth analysis and professional comparisons of the latest home security technology. We test so you can decide with confidence.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date"
      }} className="posts-grid-wrapper">
          <PostsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}